// src/features/media/components/AddCaptionModal.tsx

import { useState, useMemo } from 'react';
import { Modal, Stack, Select, FileInput, Button, Group, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileText } from '@tabler/icons-react';
import { getLangInfos, langCodeToInfo } from '@lib2/model/common/language/Languages';
import { useCreateTextTrack } from '../useTextTrackMutations';
import type { ITextTrackData, TextTrackCreateRequest } from '@/lib/types/Media';

interface AddCaptionModalProps {
  onClose: () => void;
  mediaId: number;
  existingTracks: ITextTrackData[];
}

export function AddCaptionModal({ onClose, mediaId, existingTracks }: AddCaptionModalProps) {
  const createTextTrack = useCreateTextTrack(mediaId);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One track per language — exclude languages that already have any track
  const availableLanguages = useMemo(() => {
    const existingLangCodes = new Set(existingTracks.map(t => t.langCode));
    return getLangInfos(existingLangCodes);
  }, [existingTracks]);

  const [langCode, setLangCode] = useState<string>(availableLanguages[0]?.iso2 ?? '');

  const languageData = availableLanguages.map(lang => ({
    value: lang.iso2,
    label: `${lang.nativeLabel} (${lang.englishLabel})`,
  }));

  const handleUpload = async () => {
    if (!file || !langCode) return;
    setError(null);

    const label = langCodeToInfo.get(langCode)?.nativeLabel ?? langCode;

    const req: TextTrackCreateRequest = {
      ingestMethodType: 'dashboard',
      downloadUrl: null,
      format: 'vtt',
      langCode,
      label,
      ttType: 'captions',
      remoteUrl: null,
    };

    try {
      await createTextTrack.mutateAsync({ req, file, contentType: 'text/vtt' });
      notifications.show({
        title: 'Caption Added',
        message: `${label} caption uploaded successfully`,
        color: 'green',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title="Add Caption"
      size="sm"
      closeOnClickOutside={!createTextTrack.isPending}
      closeOnEscape={!createTextTrack.isPending}
    >
      <Stack gap="md">
        {availableLanguages.length === 0 ? (
          <Alert color="blue" variant="light">
            All supported languages already have captions.
            Delete an existing caption to replace it.
          </Alert>
        ) : (
          <>
            <Select
              label="Language"
              required
              data={languageData}
              value={langCode}
              onChange={(val) => val && setLangCode(val)}
              searchable
            />

            <FileInput
              label="Caption File"
              required
              placeholder="Click to browse"
              accept=".vtt,text/vtt"
              value={file}
              onChange={setFile}
              leftSection={<IconFileText size={16} />}
              description="Accepted format: .vtt (WebVTT)"
            />
          </>
        )}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={createTextTrack.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            loading={createTextTrack.isPending}
            disabled={!file || !langCode || availableLanguages.length === 0}
          >
            Upload Caption
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
